import axios from "axios";
import tokenManager from "../security/tokenManager";
import { extractClientErrorMessage } from "./buildSupportErrorReport";
import { rememberReportableError } from "./sanitizeErrorForReport";
import { offerSupportErrorReport } from "./supportErrorReportEvents";

const LOGIN_PATHS = new Set(["/login", "/anmalning", "/data-deletion"]);

/** Background GETs that swallow errors — do not prompt members. */
const SILENT_GET_API_PATHS = ["/api/messages/unread-count"];

function sanitizeRequestUrl(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const base = process.env.REACT_APP_CONNECTION_API || "";
    const full = url.startsWith("http") ? url : `${base}${url.startsWith("/") ? url : `/${url}`}`;
    const parsed = new URL(full, typeof window !== "undefined" ? window.location.origin : undefined);
    parsed.search = "";
    let path = parsed.pathname;
    if (path.length > 120) path = `${path.slice(0, 117)}...`;
    return path;
  } catch {
    return String(url).split("?")[0].slice(0, 120);
  }
}

function isLoginOrPublicRoute() {
  if (typeof window === "undefined") return true;
  const path = window.location.pathname || "";
  for (const p of LOGIN_PATHS) {
    if (path === p || path.startsWith(`${p}/`)) return true;
  }
  return false;
}

function shouldOfferSupportReport(error) {
  const config = error?.config;
  if (config?.skipSupportErrorReport) return false;
  if (axios.isCancel(error)) return false;
  if (!tokenManager.getToken()) return false;
  if (isLoginOrPublicRoute()) return false;

  const status = error?.response?.status;
  if (status === 401) return false;

  const url = String(config?.url || "");
  if (url.includes("/api/support/bug-report")) return false;
  if (url.includes("/api/support/erasure-request")) return false;

  const method = (config?.method || "get").toLowerCase();
  if (method === "get") {
    if (config?.supportErrorReport === true) return true;
    if (SILENT_GET_API_PATHS.some((p) => url.includes(p))) return false;
    return true;
  }

  return true;
}

/**
 * Offer support consent dialog after a failed API call (logged-in app routes only).
 */
export function maybeOfferSupportErrorFromApiFailure(error) {
  if (!shouldOfferSupportReport(error)) return;

  rememberReportableError(error);

  const config = error?.config || {};
  const method = (config.method || "request").toUpperCase();
  const path = sanitizeRequestUrl(config.url);
  const page =
    (typeof window !== "undefined" && window.__NEWERA_SELECTED_PAGE__) ||
    "";
  const serverMsg = extractClientErrorMessage(error);
  const userMessage =
    config.supportErrorUserMessage ||
    serverMsg ||
    error?.message ||
    "Something went wrong.";

  offerSupportErrorReport({
    page,
    action: `${method} ${path}`.trim(),
    userMessage,
    error,
    context: {
      httpMethod: method,
      apiPath: path,
      httpStatus: error?.response?.status ?? "",
    },
  });
}

export function installSupportErrorReportAxiosInterceptor(axiosInstance) {
  if (!axiosInstance || axiosInstance.__neweraSupportErrorInterceptor) return;
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      maybeOfferSupportErrorFromApiFailure(error);
      return Promise.reject(error);
    }
  );
  axiosInstance.__neweraSupportErrorInterceptor = true;
}
